import dayjs from 'dayjs';
import { FindCooperationWarehouseByIdResponse } from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import { SiteAddressForSave } from '../components/PickupSiteFormDrawer';
import { WarehouseType, PickupSiteFormValues } from '../types/pickupSite';

/** 国家码规范成 "+1"（后端可能给数字 1 / 字符串 "1"），与表单 Select value 对齐。 */
export const normalizeCc = (cc?: string | number): string =>
  cc != null && `${cc}`.trim() !== '' ? `+${`${cc}`.replace(/^\+/, '')}` : '+1';

type CwDetail = NonNullable<FindCooperationWarehouseByIdResponse['getCooperationWarehouseById']>;

/** cw.standardize_status = 1 标准化成功（0 未标准化 / 2 标准化失败） */
const STANDARDIZE_SUCCESS = 1;

/**
 * 「地址标准化」的**唯一判据**，所有站点抽屉入口共用（审核池查看/更新原站、站点管理、司机反馈、AB 向导）。
 * 此前各入口各写一套（有的看条目 diagnosisType、有的看 standardize_status=2、有的硬编码），
 * 于是出现「卡片标着标准化失败、点进详情却是绿色已标准化」这类自相矛盾。
 *
 * <p>口径：**只有 `standardize_status === 1` 才算已标准化**。0 的语义是「从未经 api-server 保存过、
 * 状态未知」——这一列是 2.0 新增的 DEFAULT 0、无存量回填，且审核池 resolve 走的是 pickup-service
 * 窄列 UPDATE（不写这一列），所以 0 占绝大多数。按业务口径 0 一律视为未标准化、需提醒运营去标准化。
 *
 * <p>`link_status = 5`（确认无法建联）**不在这里豁免**，由调用方改传 `skipStandardize` 处理：
 * 那类站点的地址本就标准化不了（审核池两次电联未果、免上门直达终态），却要保留下来等以后联系上再转
 * 已建联。若靠本函数返回 true 会显示绿色「已标准化」——那是骗人；正确做法是走 skipStandardize
 * 通路：隐藏标准化按钮 + 抑制提示 + 放行保存门禁 + 用原 hash 落库。
 */
export const isAddressStandardized = (standardizeStatus?: number | null): boolean =>
  standardizeStatus === STANDARDIZE_SUCCESS;

/**
 * `HH:mm` / `HH:mm:ss` → TimePicker 能吃的 dayjs（拼一个固定日期，只有时分有意义）。
 *
 * <p>排期行现在有两组时间（期望上门 + 营业，2.2 需求点3），四处转换逻辑一致，抽出来避免写歪一处。
 * 返回类型写成 `string` 是沿用 {@link StandingScheduleRow} 的「表单内是 dayjs、落库是 string」两态约定。
 */
const toTimeValue = (raw?: string | null): string | undefined =>
  raw ? (dayjs(`2000-01-01T${raw}`) as unknown as string) : undefined;

/**
 * **存量回显**的时间取值，三级回落：首选值 → 回落值 → 固定默认（`09:00`/`18:00`）。
 * 营业时间与期望上门时间共用（2.2 需求点3，产品 2026-10-13 确认两者同口径）。
 *
 * **最后那一级兜底不是凭空编的**：后端 `StandingPickupBackfillJobService` 与
 * `PickupAppointmentApplicationV2Impl` 的 `DEFAULT_WORK_START/END_TIME` 现在就是 09:00-18:00，
 * 站点没配时间时系统实际就按这个值建单 —— 表单显示的即系统正在用的，落库是行为等价。
 *
 * **第三级在当前数据下一次都不会命中**（2026-10-13 实测）：灰度 hub `work_start_time` 为 NULL 的
 * 有 56 个，但它们的 `expected_pickup_start_time` 全都有值，而回落链是 `work ?? expected`，
 * 所以 `legacyStart` 恒非空。保留它是**防御性兜底** —— 两组时间本次都改成必填，一旦出现
 * 「两列都为空」的站点，运营会既看不出哪里没填、也无法自救（CLAUDE.md 四·3 要求存量数据有默认值）。
 *
 * ⚠️ 别把这条当成「修复了 56 个站点」—— 那是 P1-B 当时的误判（只看了 `work_*` 一列，
 *    没看它会回落到 `expected_*`）。真实命中数是 0。
 *
 * ⚠️ **只用于存量回显，不要用在新建上**：PRD「增量数据：营业时间 / 期望上门时间默认为空，
 * 新增时需要填写」，新建路径的 `newScheduleRow` 必须保持空值。
 */
const toTimeOrDefault = (primary?: string | null, fallback?: string | null, def = '09:00'): string =>
  toTimeValue(primary) ?? toTimeValue(fallback) ?? (dayjs(`2000-01-01T${def}`) as unknown as string);

/**
 * 既有站点(cw) → 表单值 + 地址。历史存量的"查看/更新"、司机反馈"查看站点"共用：
 * 从既有站点带出原始信息（含 2.0 字段）。方案A 回读：inBlacklist=true 联系人回填「黑名单配置」区。
 */
export function mapCwToForm(v: CwDetail): {
  values: Partial<PickupSiteFormValues>;
  address: SiteAddressForSave;
} {
  // 解析揽收排期 JSON（[{weekdays,startTime,endTime,vehicleTypeId}]），时间用 ISO 拼接转 dayjs 供 TimePicker 展示。
  // 车型口径已由 vehicle_model 切到 vehicle_type：历史行里的 vehicleModelId 与新 id 空间不通
  // （实测同一站点 model=4 / type=6），不做映射、留空由运营重选，避免回显成错误车型进而派错车。
  // todo wuhao 上线前需初始化存量 standing_schedule_json：把行内 vehicleModelId 按
  // vehicle_model → vehicle_type 映射转成 vehicleTypeId，否则历史站点编辑时车型显示为空。
  let standingSchedules: PickupSiteFormValues['standingSchedules'];
  if (v.standingScheduleJson) {
    try {
      const rows = JSON.parse(v.standingScheduleJson) as Array<{
        weekdays?: number[];
        startTime?: string;
        endTime?: string;
        businessStartTime?: string;
        businessEndTime?: string;
        vehicleTypeId?: number;
      }>;
      standingSchedules = rows.map((r) => ({
        weekdays: r.weekdays,
        // ⚠️ 期望上门时间**刻意不兜底**，缺值就让它空着、由必填规则拦住。
        // 这一分支走的是 standing_schedule_json，**不读站点级列**；而 buildSaveData 会把排期首行的
        // startTime 回写进 work_start_time 与 expected_pickup_*。若这里兜成 09:00，运营看到的是
        // 一个「已填好」的表单，一保存就把站点上真实的 work_start_time 静默改成 09:00。
        // 空着至少让运营知道这里需要他确认。（必填规则 expectedTimeRequired 是 2.2 之前就有的，非本次新增）
        startTime: toTimeValue(r.startTime),
        endTime: toTimeValue(r.endTime),
        // 营业时间（2.2 需求点3）缺失时回落到期望上门时间：2.2 之前两者本就是同一个值，
        // 这既是 PRD「历史数据：营业时间 = 原有的工作时间自动填充」的惰性迁移（编辑一次即落库），
        // 也让新增的必填规则不会把存量站点的编辑卡死 —— 打开表单就是填好的。
        businessStartTime: toTimeOrDefault(r.businessStartTime, r.startTime, '09:00'),
        businessEndTime: toTimeOrDefault(r.businessEndTime, r.endTime, '18:00'),
        vehicleTypeId: r.vehicleTypeId != null ? Number(r.vehicleTypeId) : undefined,
      }));
    } catch {
      standingSchedules = undefined;
    }
  }
  // 排期块现在长期/非长期共用：历史非长期站点的数据只存在站点级列上，
  // 迁移成一行排期（工作日 → 周期、期望上门时间、车型），否则编辑时这些值不可见也会被覆盖丢失。
  if (!standingSchedules?.length) {
    const legacyWeekdays = v.workWeekdayList ?? undefined;
    // 优先取 work_start/end_time 而非 expected_pickup_*_time：长期揽收预约单由 pickup-service 的
    // StandingPickupBackfillJobService 生成，它只读 work_start/end_time（同时写进预约单的工作时间与
    // 期望上门时间），**完全不读 expected_pickup_*_time**。两列在存量上并不总是相等（实测有站点
    // work 09:00-18:00 而 expected 08:00-17:00），读 expected 会让运营看到的时间与预约单实际时间不一致，
    // 保存时再按看到的值双写回去就等于静默改掉了上门时间。以 work 列为准即"显示=保存=调度器读取"。
    const legacyStart = v.workStartTime ?? v.expectedPickupStartTime ?? undefined;
    const legacyEnd = v.workEndTime ?? v.expectedPickupEndTime ?? undefined;
    const legacyVehicle = v.vehicleTypeId != null ? Number(v.vehicleTypeId) : undefined;
    if (legacyWeekdays?.length || legacyStart || legacyEnd || legacyVehicle != null) {
      standingSchedules = [
        {
          weekdays: legacyWeekdays,
          // legacyStart 已是 `work_* ?? expected_*`，两列都为 NULL 才会落空 ——
          // 2026-10-13 实测灰度 hub 该交集为 **0 个站点**，所以这里的兜底是防御性的、当前不命中。
          // 正因为如此，「期望上门时间全站必填」（PRD 字面）对存量零影响，R-10 不成立。
          startTime: toTimeOrDefault(legacyStart, null, '09:00'),
          endTime: toTimeOrDefault(legacyEnd, null, '18:00'),
          // 非长期站点的历史数据同样只有一组时间，营业时间按 PRD 用 work_* 填充（与上同源）；
          // work_* 也为空时兜到 09:00-18:00 —— 那正是后端 DEFAULT_WORK_START/END_TIME 的现值
          businessStartTime: toTimeOrDefault(legacyStart, null, '09:00'),
          businessEndTime: toTimeOrDefault(legacyEnd, null, '18:00'),
          vehicleTypeId: legacyVehicle,
        },
      ];
    }
  }
  return {
    address: {
      addressLine1: v.addressLine1,
      addressLine2: v.addressLine2,
      city: v.city,
      state: v.state,
      zipcode: v.zipcode,
      country: v.country ?? 'US',
      // 统一判据（见 isAddressStandardized）：只有 standardize_status===1 才显示「已标准化」。
      // 抽屉按 `standardized !== false` 判定，所以未标准化必须显式给 false、不能留 undefined。
      standardized: isAddressStandardized(v.standardizeStatus),
    },
    values: {
      name: v.name,
      linkStatus: v.linkStatus ?? undefined,
      warehouseType: (v.warehouseCategory ?? undefined) as WarehouseType | undefined,
      brand: v.brand ?? undefined,
      subBrand: v.subBrand ?? undefined,
      dock: v.dock ?? undefined,
      groupId: v.groupId ?? undefined,
      groupName: v.groupName ?? undefined,
      // 分段地址由 PickupSiteFormBody 的播种 effect 从这四段 + dock 组装成 addrFields，此处不再拼单串
      remark: v.remark,
      otherRemark: v.otherRemark ?? undefined,
      standingPickup: v.standingPickup ?? false,
      workWeekdays: v.workWeekdayList ?? undefined,
      standingSchedules,
      // 别名必须回读：后端 alias_json 是无条件写，本表单不带它保存就会把别名清成 NULL
      aliasList: v.aliasList ?? undefined,
      // 经纬度同理（latitude/longitude 也是无条件写）。本表单不展示、不编辑，纯透传回存。
      // 走新地址库的 hub 上这里拿到的是 address_core_latitude，与老 1.0 表单同口径，见类型定义注释。
      latitude: v.latitude ?? undefined,
      longitude: v.longitude ?? undefined,
      // 车型口径为 vehicle_type：use_vehicle_model_id 与 vehicle_type_id 是两套 id 空间
      // （实测同一站点 model=4 / type=6），此前读 useVehicleModelId 去 vehicleModels 里匹配，
      // 既查错表也查错号，故回显为原始 id。GraphQL Long 可能回字符串，同渠道一样强转 number 对齐 options。
      vehicleTypeId: v.vehicleTypeId != null ? Number(v.vehicleTypeId) : undefined,
      // 废弃列原值透传，保存时原样回写（预约单链路仍在读，清空会影响派车）
      useVehicleModelId: v.useVehicleModelId != null ? Number(v.useVehicleModelId) : undefined,
      // channel_id 是 1.0 表单「长期揽收 / 预约客户」的落库列（2.0 把该概念迁到 standing_pickup_channel_id），
      // 长期揽收预约单调度器至今仍按 channel_id is null 硬过滤。本表单原先不发该字段，
      // 而 saveCooperationWarehouse 是 PUT 语义（不传即清空），编辑一次就会让站点从调度器视野里消失。
      // 与 useVehicleModelId 同处理：原值透传 + 回写；长期揽收时由 standingClientId 覆盖（两列同义）。
      channelId: v.channelId != null ? Number(v.channelId) : undefined,
      // 同为本表单不采集但下游在读的列，原值透传防止编辑后被静默清空：
      // email 进预约单 contactEmail；groupQrcodeS3Key 是建联群二维码
      email: v.email ?? undefined,
      // link5 站点编辑走 skipAddressStandardize 时必须原样回传这两个命中键，
      // 否则后端 buildAnalyzedFromRequest 取不到值、save() 全列覆盖会把站点 hash 写成 NULL
      addressCoreHash: v.addressCoreHash ?? undefined,
      addressAnalysisHash: v.addressAnalysisHash ?? undefined,
      groupQrcodeS3Key: v.groupQrcodeS3Key ?? undefined,
      // 渠道 id 经 GraphQL Long 标量回来可能是字符串（"54"），而 Select options 的 value 是 number，
      // 不强转会导致回显原始 id 而非渠道名；统一转 number 对齐 options
      relatedClientIds: v.relatedChannelIds?.map((x) => Number(x)),
      standingClientId: v.standingPickupChannelId != null ? Number(v.standingPickupChannelId) : undefined,
      // 非长期揽收的期望上门时间：与排期行同样需转 dayjs 供 TimePicker 展示，
      // 直接回原始 "HH:mm:ss" 字符串 TimePicker 认不出、编辑时会显示为空
      expectedTimeStart: v.expectedPickupStartTime
        ? (dayjs(`2000-01-01T${v.expectedPickupStartTime}`) as unknown as string)
        : undefined,
      expectedTimeEnd: v.expectedPickupEndTime
        ? (dayjs(`2000-01-01T${v.expectedPickupEndTime}`) as unknown as string)
        : undefined,
      contactPersons: (v.contactPersonList ?? [])
        .filter((c) => !c.inBlacklist)
        .map((c) => ({
          name: c.name,
          phoneCountryCode: normalizeCc(c.countryCode),
          phone: c.phone,
          extension: c.extension,
        })),
      blacklistContacts: (v.contactPersonList ?? [])
        .filter((c) => c.inBlacklist)
        .map((c) => ({
          phoneCountryCode: normalizeCc(c.countryCode),
          phone: c.phone ?? undefined,
          extension: c.extension ?? undefined,
        })),
      // 关联地址（对齐生产 1.0：读 related_address_json 的 address1/address2 对，只读展示）
      relateAddresses: (v.relatedAddressList ?? [])
        .filter((r) => (r.address1 && r.address1.trim()) || (r.address2 && r.address2.trim()))
        .map((r) => ({
          address1: r.address1 ?? undefined,
          address2: r.address2 ?? undefined,
        })),
    },
  };
}
